import util from 'Utils';
import { createSelector } from 'reselect';
import {
  indicatorsWithRouteState,
} from './getIndicators';
import getCategoriesWithParents from '../getCategoriesWithParents';
import ordinalSafetyCheck from '../../services/ordinalSafetyCheck';

import {
  getActiveGeos,
  getRouter,
} from './basic';

const { geosAsObject } = util;
const { mapKV } = util.obj;
const { groupBy } = util.array;
const geoMap = geosAsObject(true);

/**
 * Sanity function for generating cat part of sort
 * @param {array}  cats   categories
 * @param {object} item   item categories
 * @returns {string}      sortString
 */
function getCatSort(cats, item) {
  let activeCat = Array.isArray(item.categories) ?
    `${item.categories[0]}` :
    `${item.categories}`;

  // Use the id if it's a non-mega category (they have no categories).
  if (!item.categories) activeCat = item.id;
  const cat = cats.find(({ key }) => key === activeCat);

  /**
   * Children of mega indicators don't appear in cat.indicators.
   * They return -1 which leads to row ordering issues.
   * The ternary check in the return below creates a fallback to prevent these issues.
   */
  const ordinal = cat.indicators.indexOf(parseInt(item.id, 10));

  return {
    parent: cat.parent,
    key: activeCat,
    itemOrdinal: ordinalSafetyCheck(ordinal),
  };
}

/**
 * Build and assemble rows for consumption by the table
 * @param  {array[string]} geos       geographies in table
 * @param  {array[object]} indicators data for the table
 * @return {array[object]}            rows for the table
 */
// @TODO refactor this for better memorization
export function assembleRows(geos, router, ind, { categories: cats }) {
  const {
    megaIndicators = [],
    indicators = [],
  } = groupBy(ind, ({ children = false }) =>
    (children ? 'megaIndicators' : 'indicators'));

  const nonMegaCats = ind.filter((item) => item.isCat);

  const megaIndicatorDataViews =
    megaIndicators.reduce((acc, { id, fullId, children }) => {
      if (fullId) {
        return {
          [id]: router[fullId] ? router[fullId].dataView : undefined,
        };
      }

      return ({
        [id]: children
          // Filtering out children in router that don't have a dataView
          // Else, we may end up setting the dataView to undefined.
          .filter((child) => router[child] && router[child].dataView)
          .map((child) => router[child].dataView).shift(),
        ...acc,
      });
    }, {});

  const cleanedIndicators = indicators.map(({
    id,
    categories,
    sortId,
    url,
    data = [],
    name,
    state: { dataView = false, year = false } = {},
    years,
    config = {},
    hasMega = false,
    fullId,
    activeYear,
  }) => {
    const localSortId = {
      category: getCatSort(cats, { categories, id }),
      id: sortId || id,
      fullId,
    };
    const megaDataView = hasMega ? megaIndicatorDataViews[hasMega] : false;
    if (hasMega) {
      const mega = megaIndicators
        .find(({ children }) => -1 < children.indexOf(parseInt(id, 10)));
      const nonMegCat = nonMegaCats
        .find(({ indicators: inds }) => -1 < inds.indexOf(parseInt(id, 10)));

      localSortId.category = getCatSort(
        cats,
        {
          categories,
          id: mega ? mega.id : nonMegCat.id,
        }
      );

      /**
       * Children of mega indicators don't appear in cat.indicators.
       * They return -1 which leads to row ordering issues.
       * The ternary check below creates a fallback to prevent these issues.
       */
      const ordinal = mega ?
        mega.children.indexOf(parseInt(id, 10)) :
        nonMegCat.indicators.indexOf(parseInt(id, 10));
      localSortId.itemOrdinal = ordinalSafetyCheck(ordinal);
    }
    const { globalRules: { locked = false } = {} } = config;

    // iterate through indicators and set active (state or default)
    const active = {};
    const { dataViews = [] } = data.find(({ dataViews: dv }) => dv) || {};
    active.dataView =
      dataView || megaDataView || (dataViews.length && dataViews[0].comparator);
    active.year = year || activeYear;

    const fallbackDataView = dataViews.length ? dataViews[0].comparator : false;
    const foundDataView =
      dataViews.find((x) => (dataView || megaDataView) === x.comparator);

    const dataViewToCompare =
      (dataView || megaDataView) && 'undefined' !== typeof foundDataView ?
        active.dataView : fallbackDataView;

    // set columns based on active geos
    const filteredData = data.map((x) => {
      const location = -1 < (x.location || '').indexOf('District of Columbia') ?
        'Dist. of Columbia' :
        x.location;
      return !location ?
        x :
        ({ ...x, location: geoMap[location.trim()] });
    }).filter(({ location }) => (-1 < geos.indexOf(location))) // filter out headers
    .reduce((acc, row) => (
      // We show all data views in a specific year
      acc.concat(...row.values
        .filter(({ year: yr }) => yr === active.year) // filter out inactive years
        .filter(({ comparator: comp }) => { // filter out wrong data types
          if (dataViewToCompare && 1 < row.dataViews.length) {
            return comp === dataViewToCompare; // if we have a type to compare against, compare it
          }
          return true; // if we have no comparison type, don't filter anything out
        })
        .map((value) => ({
          ...row,
          ...value,
          type: value.type,
        }))
      )
    ), []);

    const pivoted = mapKV(
      groupBy(filteredData, ({ characteristic }) => characteristic),
      (rowData, characteristic) => ({
        characteristic,
        indicator: id,
        sortId: {
          ...localSortId,
          row: `${rowData[0].characteristicIdx}-${rowData[0].type}`,
        },
        ...rowData.reduce((
          acc,
          { location, value, align, formatted, footnotes = [] }
        ) => ({
          ...acc,
          [location]: { value, align, formatted, footnotes },
        }), {}),
      }),
      true
    );

    const filteredPivot = pivoted.filter(({ characteristic, indicator }) => {
      // Distinguish between multi category indicators, if they exist.
      const conditionalId = fullId !== undefined ? fullId : indicator;
      return !(
        router[conditionalId] &&
        router[conditionalId].distribution &&
          (router[conditionalId].distribution === characteristic ||
          -1 < router[conditionalId].distribution.indexOf(characteristic))
      );
    });

    let facetCount = 0;
    facetCount += dataViews && 1 < dataViews.length ? dataViews.length : 0;
    facetCount += years && 1 < years.length ? years.length : 0;

    return {
      id,
      url,
      data: filteredPivot.map((item) => ({ ...item, count: pivoted.length })),
      name,
      years,
      active,
      locked,
      sortId: localSortId,
      hasMega,
      dataViews,
      facetCount,
      full: true,
      fullId,
      characteristicCount: (1 < pivoted.length ? dataViews.length || 0 : 0),
      // attach notes & sources from config
      notes: config.notesSources,
    };
  });

  // explode arrays out into full rows for megas/indicators + data
  return cleanedIndicators
    .reduce((acc, x) => acc.concat(x, ...x.data), [])
    .concat(megaIndicators.map((x) => {
      const children =
        cleanedIndicators.filter(({ hasMega }) => x.id === hasMega);

      return {
        ...x,
        sortId: {
          category: getCatSort(cats, x),
          id: x.id,
          string: `${getCatSort(cats, x)}-${x.sortId || x.id}`,
        },
        children,
        full: true,
        facetCount: children.reduce(
          (acc, { facetCount = 0 } = {}) => acc + facetCount, 0),
        characteristicCount: children.reduce(
          (acc, { characteristicCount = 0 } = {}) =>
            acc + characteristicCount, 0),
      };
    }));
}

const getRows = createSelector(
  [
    getActiveGeos,
    getRouter,
    indicatorsWithRouteState,
    getCategoriesWithParents,
  ],
  assembleRows,
);

export default getRows;



// WEBPACK FOOTER //
// ./app/selectors/table/getRows.js